import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingCart, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/accessories", label: "Accessories" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 glass bg-white/80 border-b border-black/10">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="Auckland E-Kits home">
          <Logo />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1).replace("/", "-")}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive ? "text-volt" : "text-zinc-500 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/cart"
            data-testid="cart-link"
            className="relative flex items-center gap-2 rounded-[3px] border border-black/15 px-4 py-2.5 text-ink hover:border-volt hover:text-volt transition-colors duration-200"
            aria-label={`Cart, ${count} items`}
          >
            <ShoppingCart className="h-4 w-4" strokeWidth={2.2} />
            <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.2em]">Cart</span>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={count}
                data-testid="cart-count"
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1 font-mono text-[10px] font-bold ${
                  count > 0 ? "bg-volt text-white" : "bg-zinc-300 text-zinc-600"
                }`}
              >
                {count}
              </motion.span>
            </AnimatePresence>
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-[3px] border border-black/15 text-ink hover:border-volt hover:text-volt transition-colors duration-200"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden border-t border-black/10 bg-white/95"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1).replace("/", "-")}`}
                  className={({ isActive }) =>
                    `py-3.5 font-display text-lg font-bold tracking-tight border-b border-black/5 last:border-0 ${
                      isActive ? "text-volt" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
